/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { ReviewCard } from "./ReviewCard";
import * as S from "./styles";
import { DecorativeDot } from "../../../lib/styles";
import {
  MOBILE_BREAKPOINT,
  HOMEPAGE_ANCHOR_CONTAINER_IDs,
} from "../../../lib/constants";

const testimonialsData = [
  {
    orderNum: 3,
    text: (
      <>
        As a campaign professional, I have used myriad different communications{" "}
        tools for my outreach efforts. I recently discovered{" "}
        <span className="text-highlight">ReachthePeople.ai</span> and it has{" "}
        immediately improved my ability to engage constituents and potential{" "}
        voters. I highly recommend this new and innovative AI tool.
      </>
    ),
    name: "Mary Buell",
    title: "Campaign Manager",
  },
  {
    orderNum: 1,
    text: (
      <>
        <span className="text-highlight">ReachthePeople.ai</span> should be an{" "}
        integral part of construction policy organizations seeking to influence{" "}
        legislation and mobilize industry stakeholders. Its AI-driven platform{" "}
        enables efficient, targeted outreach to constituents, making it easier{" "}
        to rally support for infrastructure initiatives, permitting reforms, and{" "}
        workforce development policies. This tool is especially effective for{" "}
        amplifying the voice of the construction industry in policymaking.
      </>
    ),
    name: "Michael Cooperman",
    title: "Real Estate and Construction",
  },
  {
    orderNum: 2,
    text: (
      <>
        We hadn’t run a campaign like this before, so I wasn’t sure what to{" "}
        expect — but the results blew me away. Using{" "}
        <span className="text-highlight">ReachthePeople.ai</span>, we saw an{" "}
        incredible 5% engagement rate right out of the gate and an immediate{" "}
        increase in donations. It took just minutes to set up and launch, and{" "}
        the platform made it effortless to connect with our supporters in a{" "}
        truly personal, meaningful way. We’ll be using it again for future{" "}
        campaigns!
      </>
    ),
    name: "Jo Haskin",
    title: "Treasurer - Reel American Heroes",
  },
  {
    orderNum: 4,
    text: (
      <>
        I highly recommend{" "}
        <span className="text-highlight">ReachthePeople.ai</span> as an{" "}
        effective messaging tool for lobbyists looking to mobilize constituents{" "}
        and influence public policy. Its AI-powered platform streamlines{" "}
        personalized outreach, making it easy to connect with thousands of{" "}
        voters or stakeholders at scale. The tool is especially valuable for{" "}
        driving targeted advocacy campaigns and amplifying grassroots support.
      </>
    ),
    name: "Morgan. P. Muchnick",
    title: "Communications & Government Affairs",
  },
].sort((a, b) => a.orderNum - b.orderNum);

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function TestimonialsSection() {
  const isMobile = useMediaQuery({ maxWidth: MOBILE_BREAKPOINT });

  const defaultActiveCardIndex = 1;

  const [activeIdx, setActiveIdx] = useState(defaultActiveCardIndex);
  const gridRef = useRef(null);

  const dotsCount = testimonialsData.length;

  const handlePrev = () => {
    setActiveIdx((prevIndex) =>
      prevIndex === 0 ? dotsCount - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setActiveIdx((prevIndex) =>
      prevIndex >= dotsCount - 1 ? 0 : prevIndex + 1
    );
  };

  const currentTestimonials = [
    testimonialsData.at(activeIdx - 1),
    testimonialsData.at(activeIdx),
    testimonialsData.at(activeIdx + 1) || testimonialsData[0],
  ];

  const scrollToReviewCard = (idx, behavior) => {
    try {
      gridRef.current?.children[idx]?.scrollIntoView({
        behavior: behavior || "smooth",
        inline: "center",
        block: "nearest",
      });
    } catch (e) {
      console.error("ScrollIntoView failed:", e);
    }
  };

  const handleSelect = (idx, behavior) => {
    const newIdx = Math.max(0, Math.min(idx, testimonialsData.length - 1));

    scrollToReviewCard(newIdx, behavior);

    setActiveIdx(newIdx);
  };

  useEffect(() => {
    if (isMobile) {
      handleSelect(defaultActiveCardIndex, "auto");
    }
  }, [isMobile]);

  // --- Debounced Scroll Listener ---
  const handleScroll = useMemo(
    () =>
      debounce(() => {
        if (!gridRef.current || !isMobile) return;

        const gridElement = gridRef.current;
        const scrollLeft = gridElement.scrollLeft;
        const gridWidth = gridElement.offsetWidth;
        const viewportCenter = scrollLeft + gridWidth / 2;

        let closestIndex = -1;
        let minDistance = Infinity;

        Array.from(gridElement.children).forEach((cardElement, index) => {
          const cardLeft = cardElement.offsetLeft;
          const cardWidth = cardElement.offsetWidth;
          const cardCenter = cardLeft + cardWidth / 2;
          const distance = Math.abs(cardCenter - viewportCenter);

          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== -1 && closestIndex !== activeIdx) {
          setActiveIdx(closestIndex);
        }
      }, 150),
    [isMobile, activeIdx]
  );
  // --- End Debounced Scroll Listener ---

  useEffect(() => {
    const gridElement = gridRef.current;
    if (gridElement && isMobile) {
      gridElement.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        gridElement.removeEventListener("scroll", handleScroll);
      };
    }
    // Cleanup if isMobile becomes false or component unmounts
    return () => {
      if (gridElement) {
        gridElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [gridRef, isMobile, handleScroll]);

  return (
    <S.StyledSection id={HOMEPAGE_ANCHOR_CONTAINER_IDs.testimonials.key}>
      <S.Heading>
        Leading professionals <S.HeadingHighlight>are </S.HeadingHighlight>
        <br />
        <S.HeadingHighlight>endorsing Reach</S.HeadingHighlight>
        <DecorativeDot src="/images/icons/dot-grey.svg" alt="Decorative dot" />
      </S.Heading>
      <S.ContentWrapper>
        <S.TestimonialsGrid ref={gridRef}>
          {(isMobile ? testimonialsData : currentTestimonials).map(
            (testimonial, index) => (
              <ReviewCard
                key={index}
                text={testimonial.text}
                name={testimonial.name}
                title={testimonial.title}
                size={
                  index === (isMobile ? activeIdx : defaultActiveCardIndex)
                    ? "large"
                    : "small"
                }
                onClick={isMobile ? () => handleSelect(index) : undefined}
              />
            )
          )}
        </S.TestimonialsGrid>
        <S.ControlsContainer>
          <S.ControlButton
            onClick={handlePrev}
            aria-label="Previous testimonial"
          >
            <S.ControlButtonIcon
              src="/images/icons/arrow-left-white.svg"
              alt="Left arrow"
            />
          </S.ControlButton>
          <S.IndicatorsContainer>
            {Array.from({
              length: dotsCount,
            }).map((_, index) => (
              <S.IndicatorDot
                key={index}
                $isActive={index === activeIdx}
                onClick={() => setActiveIdx(index)}
              />
            ))}
          </S.IndicatorsContainer>
          <S.ControlButton onClick={handleNext} aria-label="Next testimonial">
            <S.ControlButtonIcon
              src="/images/icons/arrow-right-white.svg"
              alt="Right arrow"
            />
          </S.ControlButton>
        </S.ControlsContainer>
      </S.ContentWrapper>
    </S.StyledSection>
  );
}
